import { dxtToLocalServerTime } from "@dexteel/mesf-core";
import { Box, Typography } from "@mui/material";
import { Task } from "@/controls/ganttAsset/types/public-types";
import { BundleTooltip } from "@/pages/home/components/gantt-section/components/BundleTooltip";
import {
	GanttEvent,
	GanttWorkOrder,
} from "@/pages/home/components/gantt-section/models/types";
import {
	AREA_COLOR_SAWS,
	RECLASSIFIED_COLOR,
	WORKORDER_COLOR,
} from "@/shared/colors";
import { withFraction32 } from "@/utils/fraction";

interface Props {
	task: Task;
	fontSize: string;
	fontFamily: string;
}

const formatDuration = (start: Date, end: Date): string => {
	const mins = Math.max(
		0,
		Math.floor((end.getTime() - start.getTime()) / 60000),
	);
	const h = Math.floor(mins / 60);
	const m = mins % 60;
	return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

const Row = ({
	label,
	value,
}: {
	label: string;
	value: string | number | null | undefined;
}) => (
	<Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
		<Typography sx={{ color: "#666", fontSize: "14px" }}>{label}:</Typography>
		<Typography sx={{ fontWeight: "bold", fontSize: "14px" }}>
			{value ?? "-"}
		</Typography>
	</Box>
);

// Custom tooltip for the East Saw gantt bars. task.source is a GanttEvent
// (bundle) or a GanttWorkOrder (bottom band); only events carry EventType.
export const SawBundleTooltip = ({ task, fontSize, fontFamily }: Props) => {
	if (task.type === "taskLine") {
		return (
			<BundleTooltip task={task} fontSize={fontSize} fontFamily={fontFamily} />
		);
	}

	const event = task.source as GanttEvent | undefined;

	if (event?.EventType == null) {
		const wo = task.source as GanttWorkOrder | undefined;
		if (wo?.WorkOrderId == null) return null;
		return (
			<Box
				sx={{
					backgroundColor: "#fff",
					color: "#333",
					padding: 2,
					borderRadius: 1,
					minWidth: 210,
					boxShadow: 2,
					border: "1px solid #ddd",
				}}
			>
				<Typography
					variant="subtitle1"
					sx={{
						fontWeight: "bold",
						borderBottom: "1px solid #ccc",
						paddingBottom: 0.5,
						marginBottom: 1,
						color: WORKORDER_COLOR,
						fontSize: "16px",
					}}
				>
					Work Order # {wo.WorkOrderNumber ?? "—"}
				</Typography>

				<Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
					<Row label="Part Number" value={wo.PartNumber} />
					<Row label="Extended Part #" value={wo.ExtendedPartNumber} />
					<Row label="Quantity" value={wo.Quantity} />
					<Row label="Status" value={wo.Status} />
					<Row
						label="Start"
						value={dxtToLocalServerTime(task.start, "MM/dd HH:mm")}
					/>
					<Row
						label="End"
						value={dxtToLocalServerTime(task.end, "MM/dd HH:mm")}
					/>
					<Row label="Duration" value={formatDuration(task.start, task.end)} />
				</Box>
			</Box>
		);
	}

	const id = event?.BundleId ?? event?.BatchId;

	// Violet bottom-band segment: the reclassification itself, from → to.
	if (task.id.startsWith("reclassified-")) {
		return (
			<Box
				sx={{
					backgroundColor: "#fff",
					color: "#333",
					padding: 2,
					borderRadius: 1,
					minWidth: 210,
					boxShadow: 2,
					border: "1px solid #ddd",
				}}
			>
				<Typography
					variant="subtitle1"
					sx={{
						fontWeight: "bold",
						borderBottom: "1px solid #ccc",
						paddingBottom: 0.5,
						marginBottom: 1,
						color: RECLASSIFIED_COLOR,
						fontSize: "16px",
					}}
				>
					Reclassified Bundle {id}
				</Typography>

				<Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
					<Row label="From" value={event?.OriginalPartNumber} />
					<Row label="To" value={event?.PartNumber} />
					<Row label="Pieces" value={event?.BarCount} />
					<Row
						label="Start"
						value={dxtToLocalServerTime(task.start, "MM/dd HH:mm")}
					/>
					<Row
						label="End"
						value={dxtToLocalServerTime(task.end, "MM/dd HH:mm")}
					/>
				</Box>
			</Box>
		);
	}

	const bad = (event?.TooLong ?? 0) + (event?.TooShort ?? 0);
	const layers =
		event?.LayerCount != null
			? `${event.LayerCount}${event?.LayerCountPreset ? ` / ${event.LayerCountPreset}` : ""}`
			: null;

	return (
		<Box
			sx={{
				backgroundColor: "#fff",
				color: "#333",
				padding: 2,
				borderRadius: 1,
				minWidth: 210,
				boxShadow: 2,
				border: "1px solid #ddd",
			}}
		>
			<Typography
				variant="subtitle1"
				sx={{
					fontWeight: "bold",
					borderBottom: "1px solid #ccc",
					paddingBottom: 0.5,
					marginBottom: 1,
					color: AREA_COLOR_SAWS,
					fontSize: "16px",
				}}
			>
				Bundle {id}
			</Typography>

			<Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
				<Row label="Part Number" value={event?.PartNumber} />
				{(event?.IsCutback === true ||
					event?.IsReclassified === true ||
					event?.WasReclassified === true) && (
					<Row label="Original Part" value={event?.OriginalPartNumber} />
				)}
				{event?.IsCutback !== true && (
					<>
						<Row
							label="Bundle # (Shift)"
							value={event?.ShiftBundleNo ?? "In progress"}
						/>
						<Row
							label="Bundle # (Run)"
							value={
								event?.ProfileRunNo ??
								(event?.WorkOrderId == null
									? "No work order (part unknown)"
									: "In progress")
							}
						/>
						<Row
							label="Bundle # (Work Order)"
							value={
								event?.RunBundleNo ??
								(event?.WorkOrderId == null
									? "No active work order"
									: "In progress")
							}
						/>
					</>
				)}
				<Row
					label="Start"
					value={dxtToLocalServerTime(task.start, "MM/dd HH:mm")}
				/>
				<Row
					label="End"
					value={dxtToLocalServerTime(task.end, "MM/dd HH:mm")}
				/>
				<Row label="Duration" value={formatDuration(task.start, task.end)} />
				<Row label="Pieces" value={event?.BarCount} />
				<Row label="Layers" value={layers} />
				<Row
					label="Avg Length"
					value={
						event?.AvgLength != null ? withFraction32(event.AvgLength) : null
					}
				/>
				{bad > 0 && <Row label="Bad Pieces" value={bad} />}
			</Box>
		</Box>
	);
};
